import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ChangeDetectorRef,
  OnInit,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { ProjectExpensesService } from '../project-expenses.service';
import {
  InvoiceData,
  InvoiceService,
  ModalService,
  InvoicePayment,
} from '@ops/shared-types';
import { Miscellanious } from 'legacy/shared/global-config/src/lib/misc.utils';
import { TranslateService } from '@ngx-translate/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-invoice',
  templateUrl: './invoice.component.html',
  styleUrls: ['./invoice.component.scss'],
})
export class InvoiceComponent implements OnDestroy, OnInit {
  @Input() set invoice(invoice: InvoiceData) {
    if (invoice.paymentReceivedDate)
      invoice.paymentReceivedDate = new Date(invoice.paymentReceivedDate);
    this._invoice = invoice;
    this.receivedAmount = this._invoice.receivedAmount.toString();
    if (this._invoice.currencyCode) {
      this.currencySymbol = Miscellanious.currencySymbolFromCode(
        'en-US',
        invoice.currencyCode
      );
    }

    this.originalPaymentExpectedDate = this._invoice.paymentExpectedDate

    setTimeout(() => {
      this.invoice$.next(invoice);
    }, 0);
  }
  @Input() isInPayments = false;
  @Output('delete') deleteEmitter: EventEmitter<string> =
    new EventEmitter<string>();
  invoice$ = new Subject<InvoiceData>();

  _invoice: InvoiceData;
  receivedAmount: string = '';
  currencySymbol: string;
  subscriptions: Array<Subscription> = [];

  newPayment: InvoicePayment;
  newAmount: string;

  isEditMode: boolean;

  virginPaymentData: Array<InvoicePayment> = [];
  originalPaymentExpectedDate: Date | null;

  // Translation variables
  archiveConfirmMessage: string;
  archiveSuccessMessage: string;
  deleteConfirmMessage: string;
  deleteWithPaymentMessage: string;
  deleteSuccessMessage: string;
  paymentDateRequiredMessage: string;
  allPaymentsDateRequiredMessage: string;

  constructor(
    private projectExpensesService: ProjectExpensesService,
    private modalService: ModalService,
    private changeDetector: ChangeDetectorRef,
    private invoiceService: InvoiceService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit() {
    this.loadTranslations();

    // Listen for language changes
    const langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
    this.subscriptions.push(langChangeSub);
  }

  formatSentBy(sentBy: string | undefined): SafeHtml {
    if (!sentBy) {
      return '';
    }
    const username = sentBy.split('@')[0];
    const formatted = username.replace(/([.-])/g, '$1<wbr>');
    return this.sanitizer.bypassSecurityTrustHtml(formatted);
  }

  private loadTranslations() {
    const translationKeys = [
      'projectEstimate.invoice.actions.confirmPayment',
      'projectEstimate.invoice.actions.editPayments',
      'projectEstimate.invoice.actions.downloadPdf',
      'projectEstimate.invoice.actions.archive',
      'projectEstimate.invoice.actions.delete',
      'projectEstimate.invoice.alerts.archiveConfirm',
      'projectEstimate.invoice.alerts.archiveSuccess',
      'projectEstimate.invoice.alerts.deleteConfirm',
      'projectEstimate.invoice.alerts.deleteWithPayment',
      'projectEstimate.invoice.alerts.deleteSuccess',
      'projectEstimate.invoice.alerts.paymentDateRequired',
      'projectEstimate.invoice.alerts.allPaymentsDateRequired',
    ];

    this.translate.get(translationKeys).subscribe((translations) => {
      this.archiveConfirmMessage =
        translations['projectEstimate.invoice.alerts.archiveConfirm'];
      this.archiveSuccessMessage =
        translations['projectEstimate.invoice.alerts.archiveSuccess'];
      this.deleteConfirmMessage =
        translations['projectEstimate.invoice.alerts.deleteConfirm'];
      this.deleteWithPaymentMessage =
        translations['projectEstimate.invoice.alerts.deleteWithPayment'];
      this.deleteSuccessMessage =
        translations['projectEstimate.invoice.alerts.deleteSuccess'];
      this.paymentDateRequiredMessage =
        translations['projectEstimate.invoice.alerts.paymentDateRequired'];
      this.allPaymentsDateRequiredMessage =
        translations['projectEstimate.invoice.alerts.allPaymentsDateRequired'];
    });
  }

  getActionOptions() {
    const options = [] as any[];

    const hasPayments = this._invoice?.payments?.length > 0;
    const canConfirm = !this.newPayment && !hasPayments;
    const canAddPayment = hasPayments;

    options.push({
      label: this.translate.instant(
        'projectEstimate.invoice.actions.confirmPayment'
      ),
      value: 'confirmPayment',
      isDisabled: !canConfirm,
    });

    options.push({
      label: this.translate.instant('projectEstimate.invoice.actions.addPayment'),
      value: 'addPayment',
      isDisabled: !canAddPayment,
    });

    if (!this.isEditMode) {
      options.push({
        label: this.translate.instant(
          'projectEstimate.invoice.actions.editPayments'
        ),
        value: 'editPayments',
      });
    }

    if (this._invoice?.document) {
      options.push({
        label: this.translate.instant(
          'projectEstimate.invoice.actions.downloadPdf'
        ),
        value: 'downloadPdf',
      });
    }

    options.push({
      label: this.translate.instant(
        'projectEstimate.invoice.actions.archive'
      ),
      value: 'archive',
    });
    // options.push({
    //   label: this.translate.instant('projectEstimate.invoice.actions.delete'),
    //   value: 'delete',
    //   isDelete: true,
    // });
    return options;
  }

  isInvoicePaid(invoice: InvoiceData) {
    return invoice.status?.toUpperCase() === 'PAID';
  }

  updateInvoice(invoice: InvoiceData) {
    invoice.payments = invoice.payments.filter((p) => p.amount);
    const sub = this.projectExpensesService
      .updateInvoice(invoice)
      .subscribe((invoice) => {
        invoice.bankAccount = this._invoice.bankAccount;
        this.invoice = invoice;
        this.changeDetector.markForCheck();
      });
    this.subscriptions.push(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  openModal(id: string) {
    this.modalService.open(id);
  }

  onSelectExpectedDate(selection: Date) {
    this._invoice.paymentExpectedDate = new Date(selection);
  }

  onSelectPaymentDate(selection: Date, payment: InvoicePayment) {
    payment.date = new Date(selection);
  }

  onInputAmount(event: any, payment: InvoicePayment) {
    payment.amount = +event.target.value.toString().replaceAll(',', '');
    // console.log(payment);
  }

  // invoiceAmountChange(event: any) {
  //   this.changeDetector.markForCheck();
  //   this._invoice.receivedAmount = +event.target.value
  //     .toString()
  //     .replaceAll(',', '');
  //   const accountName = this._invoice.bankAccount;
  //   // this.invoice.receivedAmount =  +event.target.value
  //   // .toString()
  //   // .replaceAll(',', '');
  //   this._invoice.bankAccount = accountName;

  //   this.updateInvoice(this._invoice);
  //   // this._invoice.bankAccount = accountName;
  //   // const sub = this.projectExpensesService
  //   //   .updateInvoice(this._invoice)
  //   //   .subscribe((invoice) => {
  //   //     this.invoice = invoice;
  //   //     this._invoice.bankAccount = accountName;
  //   //   });
  //   // this.subscriptions.push(sub);
  // }

  // saveDebounced = debounce((event: any) => {
  //   this.invoiceAmountChange(event);
  // }, 500);

  selectAll(event: any) {
    setTimeout(() => event.target.select(), 1);
  }

  onSelectAction(action: string) {
    switch (action) {
      case 'archive':
        {
          if (
            window.confirm(
              this.translate.instant('windowConfirm.invoice.archiveInvoice')
            )
          ) {
            const sub = this.projectExpensesService
              .archiveInvoice(this._invoice)
              .subscribe(() => {
                window.alert(
                  this.translate.instant(
                    'windowAlert.invoice.archiveSuccess'
                  )
                );
                this.deleteEmitter.emit(this._invoice._id);
              });
            this.subscriptions.push(sub);
          }
        }
        break;
      case 'confirmPayment':
        this.virginPaymentData = [...this._invoice.payments];
        this.newPayment = {
          date: new Date(),
          amount: this._invoice.invoiceAmount - this._invoice.receivedAmount,
        };
        this.newAmount = this.newPayment.amount.toString();
        break;
      case 'addPayment':
        this.isEditMode = true;
        this.originalPaymentExpectedDate = this._invoice.paymentExpectedDate
          ? new Date(this._invoice.paymentExpectedDate)
          : null;
        this.virginPaymentData = [...this._invoice.payments];
        this.newPayment = {
          date: new Date(),
          amount: this._invoice.invoiceAmount - this._invoice.receivedAmount,
        };
        this.newAmount = this.newPayment.amount.toString();
        break;
      case 'editPayments':
        this.isEditMode = true;
        this.originalPaymentExpectedDate = this._invoice.paymentExpectedDate
          ? new Date(this._invoice.paymentExpectedDate)
          : null;
        this.virginPaymentData = [...this._invoice.payments];
        break;
      case 'downloadPdf':
        {
          const sub = this.invoiceService
            .downloadInvoice(
              this._invoice._id,
              this._invoice.document._id,
              this._invoice.project
            )
            .subscribe({
              next: (blob: Blob) => {
                let objectURL = URL.createObjectURL(blob);
                const downloadLink = document.createElement('a');
                downloadLink.target = '_self';
                downloadLink.href = objectURL;
                downloadLink.download = this._invoice.invoiceNumber ?? '';
                document.body.appendChild(downloadLink);
                downloadLink.click();
              },
              error: (error) => {
                window.alert(error.error.message);
              },
            });
          this.subscriptions.push(sub);
        }
        break;
      // case 'delete':
      //   {
      //     if(this._invoice.payments?.length > 0 ) {
      //       window.alert(this.deleteWithPaymentMessage);
      //       return;
      //     }
      //     if ( window.confirm(this.deleteConfirmMessage)) {
      //       const sub = this.projectExpensesService
      //         .deleteInvoice(this._invoice.project, this._invoice._id)
      //         .subscribe(() => {
      //           window.alert(this.deleteSuccessMessage);
      //           this.deleteEmitter.emit(this._invoice._id);
      //         });
      //       this.subscriptions.push(sub);
      //     }
      //   }
      //   break;
    }
    this.modalService.close('action-' + this._invoice._id);
  }

  get isReceivedDateVisible() {
    if (this.isInPayments) return 'visible';
  }

  isReceivedAmountVisible() {
    const classNames = [
      !this._invoice.receivedAmount && this.isInPayments
        ? 'with-input-active'
        : '',
      // @ts-ignore
      'with-input' ? this.isInPayments : '',
    ];
    return classNames.join(' ');
  }

  isInvalidDate = (valToCheck: any) => {
    return valToCheck instanceof Date && isNaN(valToCheck.getTime());
  };

  onSave() {
    if (
      this.newPayment &&
      (!this.newPayment.date || this.isInvalidDate(this.newPayment.date))
    ) {
      window.alert(this.paymentDateRequiredMessage);
      return;
    }

    if (
      this.isEditMode &&
      this._invoice.payments.some((p) => p.amount > 0 && !p.date)
    ) {
      window.alert(this.allPaymentsDateRequiredMessage);
      return;
    }

    if (this.newPayment) {
      this._invoice.payments = [...this._invoice.payments, this.newPayment];
    }

    if (!this._invoice.payments?.length) {
      this._invoice.receivedAmount = 0;
    } else {
      this._invoice.receivedAmount = this._invoice.payments.reduce(
        (s, i) => s + i.amount,
        0
      );
    }

    // console.log(this._invoice);
    this.originalPaymentExpectedDate = this._invoice.paymentExpectedDate;
    this.updateInvoice(this._invoice);
    this.onCancel();
  }
  onCancel() {
    this.isEditMode = false;
    this.newPayment = null;
    this.newAmount = '0';

    this._invoice.payments = [...this.virginPaymentData];

    this._invoice.paymentExpectedDate = this.originalPaymentExpectedDate;

    this.changeDetector.markForCheck();
  }

  get paymentReceivedDates(): Array<Date> {
    return this.paymentReceivedDates;
  }

  get receivedDateRed() {
    return !this._invoice.paymentReceivedDate && this._invoice.receivedAmount;
  }

  get receivedAmountRed() {
    return this._invoice.paymentReceivedDate && !this._invoice.receivedAmount;
  }

  get invoiceStatus() {
    this._invoice.status =
      this._invoice.receivedAmount === 0
        ? 'Sent'
        : this._invoice.receivedAmount < this._invoice.invoiceAmount
        ? 'Partial'
        : this._invoice.receivedAmount === this._invoice.invoiceAmount
        ? 'Paid'
        : 'Overpaid';

    return this._invoice.status;
  }
}
